import type { Ref } from 'vue'
import { ref } from 'vue'
import cloneDeep from 'lodash/cloneDeep'
import type { ReactiveHead, ResolvableLink } from '@unhead/vue'
import type { RootData } from '~/definitions/AllDefinitions'
import { getTheme } from '~/classes/Utils'

interface MetaIcon {
  href: string
  rel: string
  sizes?: string
  key?: string
  type?: string
}

export function useSiteSeo(rootData: Ref<RootData>, structuredData: Ref<any>) {
  const meta = ref<ReactiveHead>({})
  useHead(meta)

  function setMetaData() {
    const icons = [] as MetaIcon[]
    const lang = rootData.value.language
      ? rootData.value.language
      : 'en-US'
    meta.value.htmlAttrs = { lang, translate: 'no' }
    if (rootData.value.id) {
      meta.value.title =
        `${rootData.value.seoMeta?.title ? rootData.value.seoMeta?.title + ' | ' : ''}${rootData.value.name}`
      const metaTags: Record<string, string>[] = [
        {
          key: 'google',
          name: 'google',
          content: 'notranslate',
        },
        {
          key: 'og:title',
          property: 'og:title',
          content: rootData.value.seoMeta && rootData.value.seoMeta.title
            ? rootData.value.seoMeta.title
            : rootData.value.name,
        },
        {
          key: 'og:site_name',
          property: 'og:site_name',
          content: rootData.value.name,
        },
        {
          key: 'apple-mobile-web-app-title',
          name: 'apple-mobile-web-app-title',
          content: rootData.value.name.replace(/\s/g, '-'),
        },
        {
          key: 'author',
          name: 'author',
          content: rootData.value.name,
        },
        {
          key: 'theme-color',
          property: 'theme-color',
          name: 'theme-color',
          content: rootData.value.colorScheme.accentColor,
        },
      ]
      try {
        if (+import.meta.env.VITE_NO_INDEX >= 1) {
          metaTags.push({
            key: 'robots',
            property: 'robots',
            name: 'robots',
            content: 'noindex',
          })
        }
      } catch (e) {
        console.debug('unable to determine whether or not to index page', e)
      }
      if (rootData.value.seoMeta) {
        if (rootData.value.seoMeta.description) {
          metaTags.push({
            key: 'description',
            name: 'description',
            content: rootData.value.seoMeta.description,
          })
          metaTags.push({
            key: 'og:description',
            property: 'og:description',
            content: rootData.value.seoMeta.description,
          })
        }
        if (rootData.value.seoMeta.metaImage && rootData.value.seoMeta.metaImage.original) {
          let hasMetaImage = false
          if (rootData.value.seoMeta.metaImage.sizes) {
            if (rootData.value.seoMeta.metaImage.sizes.seoLandscapeImage) {
              metaTags.push({
                key: 'og:image',
                property: 'og:image',
                content: rootData.value.seoMeta.metaImage.sizes.seoLandscapeImage,
              })
              metaTags.push({
                key: 'og:image:secure_url',
                property: 'og:image:secure_url',
                content: rootData.value.seoMeta.metaImage.sizes.seoLandscapeImage,
              })
              metaTags.push({
                key: 'og:image:width',
                property: 'og:image:width',
                content: '1200',
              })
              metaTags.push({
                key: 'og:image.height',
                property: 'og:image:height',
                content: '628',
              })
              hasMetaImage = true
            }
          }
          if (hasMetaImage && rootData.value.seoMeta.metaImage.altText) {
            metaTags.push({
              key: 'og:image:alt',
              property: 'og:image:alt',
              content: rootData.value.seoMeta.metaImage.altText,
            })
          }
        }
      }
      meta.value.meta = metaTags
      const initial = rootData.value.name.substring(0, 1).toLowerCase()
      const iconSizes = [16, 32, 180, 192]
      const faviconUrl = `https://twyzle-s3-1.s3.amazonaws.com/libraries/favicon/${initial}/`
      if (rootData.value.seoMeta && rootData.value.seoMeta.favicon &&
        rootData.value.seoMeta.favicon.sizes &&
        Object.keys(rootData.value.seoMeta.favicon.sizes).length
      ) {
        const sizes = rootData.value.seoMeta.favicon.sizes
        if (sizes.s16) {
          icons.push({ href: sizes.s16, rel: 'apple-touch-icon', sizes: '16x16' })
          icons.push({ href: sizes.s16, rel: 'icon', sizes: '16x16' })
        }
        if (sizes.s32) {
          icons.push({ href: sizes.s32, rel: 'apple-touch-icon', sizes: '32x32' })
          icons.push({ href: sizes.s32, rel: 'icon', sizes: '32x32' })
        }
        if (sizes.s64) {
          icons.push({ href: sizes.s64, rel: 'apple-touch-icon', sizes: '64x64' })
          icons.push({ href: sizes.s64, rel: 'icon', sizes: '64x64' })
        }
        if (sizes.s120) {
          icons.push({ href: sizes.s120, rel: 'apple-touch-icon', sizes: '120x120' })
          icons.push({ href: sizes.s120, rel: 'icon', sizes: '120x120' })
        }
        if (sizes.favicon_152) {
          icons.push({ href: sizes.favicon_152, rel: 'apple-touch-icon', sizes: '152x152' })
          icons.push({ href: sizes.favicon_152, rel: 'icon', sizes: '152x152' })
        }
        if (sizes.s180) {
          icons.push({ href: sizes.s180, rel: 'apple-touch-icon', sizes: '180x180' })
          icons.push({ href: sizes.s180, rel: 'icon', sizes: '180x180' })
        }
        if (sizes.s192) {
          icons.push({ href: sizes.s192, rel: 'icon', sizes: '192x192' })
        } else if (sizes.favicon_192) {
          icons.push({ href: sizes.favicon_192, rel: 'icon', sizes: '192x192' })
        }
      }

      if (!icons.length) {
        iconSizes.forEach((size: number, k: number) => {
          const dim = `${size}x${size}`
          const icon = {
            href: faviconUrl,
            type: 'image/x-icon',
            rel: 'icon',
            key: '',
          } as MetaIcon
          if (k <= 2) {
            icon.href += `favicon-${dim}.png`
            icon.key = `icon-s-${dim}`
          } else {
            icon.rel = 'apple-touch-icon'
            icon.href += `apple-touch-icon-${dim}.png`
            icon.key = `icon-a-${dim}`
          }
          icons.push(icon)
        })
      }
      if (icons.length) {
        meta.value.link = cloneDeep(icons) as ResolvableLink[]
      }
      if (structuredData.value) {
        meta.value.script = [{
          innerHTML: JSON.stringify(structuredData.value),
          type: 'application/ld+json',
        }]
      }
    }
    let innerHTML = ':root{\r\n'
    innerHTML += getTheme(rootData.value.colorScheme.accentColor, 'accent')
    innerHTML += getTheme(rootData.value.colorScheme.navColor, 'navbar')
    innerHTML += '}'
    meta.value.style = [{
      innerHTML,
      key: 'tw-custom-colors',
    }]
  }

  // Run immediately for SSR
  setMetaData()

  return { refresh: setMetaData }
}
